import { DashboardGraph } from "../../graphs/dashboard_graph";
import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {scaleNumber} from "../../lq_js_common/helpers/scaling";
import {formatThroughput, formatRetransmit, lerpGreenToRedViaOrange} from "../../helpers/scaling";
import {clearDiv, simpleRow, simpleRowHtml, theading} from "../../helpers/builders";
import {applyRedactFont} from "../../helpers/redact";
import {scaleNanos} from "../../lq_js_common/helpers/scaling";
import {safeBigIntToNumber} from "../../helpers/number_coerce";
import {debugLog} from '../../helpers/debug_log';

export class ShaperCircuitLive extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.devices = [];
        // Flow state for Network Activity
        this.movingAverages = new Map();
        this.prevFlowBytes = new Map();
        this.tickCount = 0;
        this.trafficSortColumn = 'rate';
        this.trafficSortDirection = 'desc';
        this.deviceIpToName = new Map();
        this.flowSankey = null;
        this.latestFlows = null;
    }

    dispose() {
        this.devices = [];
        this.movingAverages.clear();
        this.prevFlowBytes.clear();
        this.deviceIpToName.clear();
        this.flowSankey = null;
        this.latestFlows = null;
        super.dispose();
    }

    title() {
        return "Circuit Live (Requires LibreQoS 1.5 RC2 or Higher)";
    }

    channelRequests() { return []; }

    buildContainer() {
        const base = super.buildContainer();

        // Create Bootstrap 5-style tab nav (manual toggling to avoid outer dashboard hiding)
        const nav = document.createElement("ul");
        nav.classList.add("nav", "nav-tabs");
        nav.setAttribute("role", "tablist");

        // Panels wrapper (not using .tab-content/.tab-pane to avoid conflicts)
        const content = document.createElement("div");
        content.classList.add("pt-2");

        const tabs = [
            { label: "Devices", id: "devicePanel" },
            { label: "Active Flows", id: "activeFlowPanel" },
            { label: "Network Activity", id: "networkActivityPanel" },
        ];

        const panes = {};
        const switchTo = (targetId) => {
            // Toggle nav active state
            nav.querySelectorAll("button.nav-link").forEach((btn) => {
                const isActive = btn.dataset.targetId === targetId;
                btn.classList.toggle("active", isActive);
                btn.setAttribute("aria-selected", isActive ? "true" : "false");
            });
            // Toggle visibility
            Object.keys(panes).forEach((id) => {
                if (id === targetId) {
                    panes[id].classList.remove("d-none");
                } else {
                    panes[id].classList.add("d-none");
                }
            });

            // When switching internal tabs, ensure charts resize and render
            if (targetId === 'activeFlowPanel') {
                // Lazily init sankey if needed
                const el = document.getElementById('flowSankey');
                if (!this.flowSankey && el) {
                    this.flowSankey = new FlowsSankey('flowSankey');
                }
                // Resize and render with latest data once the DOM is visible
                setTimeout(() => {
                    if (this.flowSankey) {
                        this.flowSankey.chart.resize();
                        if (this.latestFlows) {
                            this.flowSankey.update(this.latestFlows, this.deviceIpToName);
                        }
                    }
                }, 0);
            }
        };

        tabs.forEach((t, idx) => {
            // Build tab button
            const li = document.createElement("li");
            li.classList.add("nav-item");
            const btn = document.createElement("button");
            btn.type = "button";
            btn.classList.add("nav-link");
            if (idx === 0) btn.classList.add("active");
            // Add badge for Active Flows tab
            if (t.id === 'activeFlowPanel') {
                const badgeId = this.id + '_activeFlowBadge';
                btn.innerHTML = t.label + " <span class='badge bg-secondary ms-1' id='" + badgeId + "'>0</span>";
                this.activeFlowBadgeId = badgeId;
            } else {
                btn.innerText = t.label;
            }
            btn.dataset.targetId = t.id;
            btn.setAttribute("role", "tab");
            btn.setAttribute("aria-controls", t.id);
            btn.setAttribute("aria-selected", idx === 0 ? "true" : "false");
            btn.onclick = () => switchTo(t.id);
            li.appendChild(btn);
            nav.appendChild(li);

            // Build corresponding panel
            const pane = document.createElement("div");
            pane.id = t.id;
            pane.classList.add("tab-body");
            if (idx !== 0) pane.classList.add("d-none");
            panes[t.id] = pane;
            content.appendChild(pane);
        });

        base.appendChild(nav);
        base.appendChild(content);

        // Initialize static content areas for flows using pane references
        const activePane = panes["activeFlowPanel"];
        if (activePane) {
            const sankey = document.createElement('div');
            sankey.id = 'flowSankey';
            sankey.style.height = '420px';
            activePane.appendChild(sankey);
            // Do NOT instantiate the chart here; element isn't in DOM yet. See setup().
        }
        const netPane = panes["networkActivityPanel"];
        if (netPane) {
            const ctrls = document.createElement('div');
            ctrls.classList.add('mb-2', 'small');
            ctrls.innerHTML = "<label class='form-check-label small'><input id='hideSmallFlows' type='checkbox' class='form-check-input me-1'>Hide flows under 1 Mbps</label>";
            const holder = document.createElement('div');
            holder.id = 'allTraffic';
            netPane.appendChild(ctrls);
            netPane.appendChild(holder);
        }

        return base;
    }

    onMessage(msg) {
        if (msg.Data.target !== "circuit_live") {
            return;
        }
        const payload = msg.Data.blob; // CircuitLiveTransit
        this.latestFlows = payload.flows;
        if (this.devices.length === 0) {
            this.firstDevices(payload.devices);
        } else {
            this.updateDevices(payload.devices);
        }
        this.updateFlows(payload.flows);
    }

    setup() {
        super.setup();
        // Instantiate charts that require DOM presence
        if (!this.flowSankey) {
            const el = document.getElementById('flowSankey');
            if (el) {
                this.flowSankey = new FlowsSankey('flowSankey');
            }
        }
    }

    // Ensure charts render correctly when the outer dashboard tab becomes visible
    onTabActivated() {
        if (this.flowSankey) {
            setTimeout(() => {
                this.flowSankey.chart.resize();
                if (this.latestFlows) {
                    this.flowSankey.update(this.latestFlows, this.deviceIpToName);
                }
            }, 0);
        }
        // Resize device graphs as well
        if (this.devices && this.devices.length > 0) {
            this.devices.forEach((d) => {
                if (d.tpGraph && d.tpGraph.chart) d.tpGraph.chart.resize();
                if (d.rxGraph && d.rxGraph.chart) d.rxGraph.chart.resize();
                if (d.pingGraph && d.pingGraph.chart) d.pingGraph.chart.resize();
            });
        }
    }

    firstDevices(devices) {
        const panel = document.getElementById("devicePanel");
        devices.forEach((dev) => {
            // Build mapping of IP -> device name for Sankey labels
            if (dev.addresses_str) {
                dev.addresses_str.forEach((a) => {
                    const ip = String(a[0]);
                    this.deviceIpToName.set(ip, dev.device_name);
                });
            }
            let row = document.createElement("div");
            row.classList.add("row");
            let newEntry = {
                id: dev.device_id,
            };

            // Info Panel
            const card = document.createElement("div");
            card.classList.add("col-3")
            card.id = "info_" + dev.device_id;
            let name = document.createElement("h5");
            name.classList.add("redactable");
            name.innerHTML = "<i class='fa fa-computer'></i> " + dev.device_name;
            card.appendChild(name);

            let infoTable = document.createElement("table");
            infoTable.classList.add("table", "table-sm", "table-striped");
            let tbody = document.createElement("tbody");

            // MAC address
            let macRow = document.createElement("tr");
            let macHeading = document.createElement("td");
            macHeading.innerHTML = "MAC Address:";
            macRow.appendChild(macHeading);
            let macEntry = document.createElement("td");
            macEntry.id = "mac_" + dev.device_id;
            macEntry.innerText = dev.mac_address || "-";
            macRow.appendChild(macEntry);
            tbody.appendChild(macRow);

            // IP addresses
            let ipRow = document.createElement("tr");
            let ipHeading = document.createElement("td");
            ipHeading.innerHTML = "IP Addresses:";
            ipRow.appendChild(ipHeading);
            let ipEntry = document.createElement("td");
            ipEntry.id = "ips_" + dev.device_id;
            const addrList = (dev.addresses_str || []).map(a => `${a[0]}/${a[1]}`).join(', ');
            ipEntry.innerText = addrList;
            ipEntry.classList.add("redactable");
            ipRow.appendChild(ipEntry);
            tbody.appendChild(ipRow);

            // Last Ping
            let lastPingRow = document.createElement("tr");
            let lastPingHeading = document.createElement("td");
            lastPingHeading.innerHTML = "Last Ping (ms):";
            lastPingRow.appendChild(lastPingHeading);
            let lastPingEntry = document.createElement("td");
            lastPingEntry.id = "lastPing_" + dev.device_id;
            if (dev.last_ping_ms !== null) {
                lastPingEntry.innerHTML = dev.last_ping_ms.toFixed(2);
            } else {
                lastPingEntry.innerHTML = "-";
            }
            lastPingRow.appendChild(lastPingEntry);
            tbody.appendChild(lastPingRow);

            let lastRttRow = document.createElement("tr");
            let lastRttHeading = document.createElement("td");
            lastRttHeading.innerHTML = "Last RTT (ms):";
            lastRttRow.appendChild(lastRttHeading);
            let lastRttEntry = document.createElement("td");
            lastRttEntry.id = "lastRtt_" + dev.device_id;
            if (dev.median_tcp_rtt_ms !== null) {
                lastRttEntry.innerHTML = dev.median_tcp_rtt_ms.toFixed(2);
            } else {
                lastRttEntry.innerHTML = "-";
            }
            lastRttRow.appendChild(lastRttEntry);
            tbody.appendChild(lastRttRow);

            infoTable.appendChild(tbody);
            card.appendChild(infoTable);
            row.appendChild(card);

            // Throughput Graph
            const tp = document.createElement("div");
            tp.classList.add("col-3", "text-secondary");
            tp.id = "throughputGraph_" + dev.device_id;
            tp.style.height = "250px";
            tp.innerHTML = "<i class='fa fa-spinner fa-spin'></i> Loading...";
            row.appendChild(tp);

            // Retransmits Graph
            const rx = document.createElement("div");
            rx.id = "rxmitGraph_" + dev.device_id;
            rx.style.height = "250px";
            rx.classList.add("col-3", "text-secondary");
            rx.innerHTML = "<i class='fa fa-spinner fa-spin'></i> Loading...";
            row.appendChild(rx);

            // Ping Histogram
            const ping = document.createElement("div");
            ping.classList.add("col-3");
            ping.id = "pingGraph_" + dev.device_id;
            ping.style.height = "250px";
            ping.classList.add("col-3", "text-secondary");
            ping.innerHTML = "<i class='fa fa-spinner fa-spin'></i> Loading...";
            row.appendChild(ping);

            panel.appendChild(row);

            // Update the ringbuffer
            newEntry.tpGraph = new ThroughputGraph("throughputGraph_" + dev.device_id);
            newEntry.rxGraph = new CircuitRetransmitGraph("rxmitGraph_" + dev.device_id, "");
            newEntry.pingGraph = new DevicePingHistogram("pingGraph_" + dev.device_id);
            this.devices.push(newEntry);
        });
    }

    updateDevices(devices) {
        devices.forEach((dev) => {
            let ping = document.getElementById("lastPing_" + dev.device_id);
            if (ping && dev.last_ping_ms) ping.innerHTML = dev.last_ping_ms.toFixed(2);
            let rtt = document.getElementById("lastRtt_" + dev.device_id);
            if (rtt && dev.median_tcp_rtt_ms) rtt.innerHTML = dev.median_tcp_rtt_ms.toFixed(2);

            this.devices.forEach((d) => {
                if (d.id === dev.device_id) {
                    d.tpGraph.update(safeBigIntToNumber(dev.bits_per_second[0]), safeBigIntToNumber(dev.bits_per_second[1]));
                    d.rxGraph.update(dev.tcp_retransmit_pct[0], dev.tcp_retransmit_pct[1]);
                    if (dev.last_ping_ms !== null) {
                        d.pingGraph.update(dev.last_ping_ms);
                    }
                }
            });
        });
    }

    // ----- Flows (Active Flows + Network Activity) -----
    updateFlows(flows) {
        // Sankey update + Active flow count
        const badgeEl = document.getElementById(this.activeFlowBadgeId || (this.id + '_activeFlowBadge'))
                           || document.getElementById('activeFlowBadge');
        const sankeyContainer = document.getElementById('flowSankey');
        const sankeyVisible = sankeyContainer && sankeyContainer.offsetParent !== null && sankeyContainer.clientWidth > 0;
        if (this.flowSankey && sankeyVisible) {
            const count = this.flowSankey.update(flows, this.deviceIpToName);
            if (badgeEl) badgeEl.innerText = String(count);
            this.flowSankey.chart.resize();
        } else if (badgeEl) {
            // Fallback count until sankey is ready
            const THIRTY_S = 30 * 1_000_000_000;
            let active = 0;
            flows.forEach(f => {
                if (f.last_seen_nanos === undefined || f.last_seen_nanos === null) return;
                if (safeBigIntToNumber(f.last_seen_nanos) <= THIRTY_S) active++;
            });
            badgeEl.innerText = String(active);
        }

        // Table update
        this.updateTrafficTab(flows);
    }

    protoName(id) {
        const map = { 6: 'TCP', 17: 'UDP', 1: 'ICMP' };
        return map[id] || String(id);
    }

    updateTrafficTab(flows) {
        let target = document.getElementById("allTraffic");
        if (!target) return;

        let table = document.createElement("table");
        table.classList.add("table", "table-sm", "table-striped");
        let thead = document.createElement("thead");
        thead.style.fontSize = "0.8em";

        const createSortableHeader = (text, sortKey, colspan = 1) => {
            let th = theading(text, colspan);
            th.style.cursor = "pointer";
            th.onclick = () => {
                if (this.trafficSortColumn === sortKey) {
                    this.trafficSortDirection = this.trafficSortDirection === 'asc' ? 'desc' : 'asc';
                } else {
                    this.trafficSortColumn = sortKey;
                    this.trafficSortDirection = 'desc';
                }
            };
            if (this.trafficSortColumn === sortKey) {
                th.innerHTML += this.trafficSortDirection === 'asc' ? ' ▲' : ' ▼';
            }
            return th;
        };

        thead.appendChild(createSortableHeader("Protocol", "protocol"));
        thead.appendChild(createSortableHeader("Current Rate (d/u)", "rate", 2));
        thead.appendChild(createSortableHeader("Total Bytes (d/u)", "bytes", 2));
        thead.appendChild(createSortableHeader("Total Packets (d/u)", "packets", 2));
        thead.appendChild(createSortableHeader("TCP Retransmits % (d/u)", "retransmits", 2));
        thead.appendChild(createSortableHeader("RTT (d/u)", "rtt", 2));
        thead.appendChild(createSortableHeader("ASN", "asn"));
        thead.appendChild(createSortableHeader("Country", "country"));
        thead.appendChild(createSortableHeader("Remote IP", "ip"));
        table.appendChild(thead);
        let tbody = document.createElement("tbody");
        const thirty_seconds_in_nanos = 30_000_000_000;
        this.tickCount++;
        let hideSmallFlows = document.getElementById("hideSmallFlows");
        hideSmallFlows = hideSmallFlows ? hideSmallFlows.checked : false;

        // Prepare moving average seeds
        const seenKeys = new Set();
        flows.forEach((flow) => {
            const flowKey = `${flow.ip_protocol}-${(flow.remote_ip_str || flow.remote_ip)}-${flow.dst_port}`;
            seenKeys.add(flowKey);
            let rate = (flow.rate_bps[0] || 0) + (flow.rate_bps[1] || 0);
            if (this.prevFlowBytes.has(flowKey)) {
                const prev = this.prevFlowBytes.get(flowKey);
                const curDownBytes = safeBigIntToNumber(flow.bytes_sent[0]);
                const curUpBytes = safeBigIntToNumber(flow.bytes_sent[1]);
                const down = (curDownBytes - prev[0]) * 8;
                const up = (curUpBytes - prev[1]) * 8;
                rate = Math.max(0, down + up);
            }
            if (this.movingAverages.has(flowKey)) {
                const avg = this.movingAverages.get(flowKey);
                avg.push(rate); if (avg.length > 10) avg.shift();
                this.movingAverages.set(flowKey, avg);
            } else {
                this.movingAverages.set(flowKey, [rate]);
            }
        });
        this.evictStaleFlowState(seenKeys);

        // Build rows
        const rows = [];
        flows.forEach((flow) => {
            const flowKey = `${flow.ip_protocol}-${(flow.remote_ip_str || flow.remote_ip)}-${flow.dst_port}`;
            let down = flow.rate_bps[0] || 0;
            let up = flow.rate_bps[1] || 0;
            if (this.prevFlowBytes.has(flowKey)) {
                const prev = this.prevFlowBytes.get(flowKey);
                const ticks = this.tickCount - prev[2];
                const curDownBytes = safeBigIntToNumber(flow.bytes_sent[0]);
                const curUpBytes = safeBigIntToNumber(flow.bytes_sent[1]);
                let d = (curDownBytes - prev[0]) * 8;
                let u = (curUpBytes - prev[1]) * 8;
                if (ticks > 1) { d = d / ticks; u = u / ticks; }
                down = Math.max(0, d); up = Math.max(0, u);
            }
            const curDownBytesStore = safeBigIntToNumber(flow.bytes_sent[0]);
            const curUpBytesStore = safeBigIntToNumber(flow.bytes_sent[1]);
            this.prevFlowBytes.set(flowKey, [ curDownBytesStore, curUpBytesStore, this.tickCount ]);
            const lastSeen = safeBigIntToNumber(flow.last_seen_nanos);
            if (lastSeen > thirty_seconds_in_nanos) return;

            const visible = !hideSmallFlows || (down > 1024*1024 || up > 1024*1024);
            const pktDown = safeBigIntToNumber(flow.packets_sent[0]);
            const pktUp = safeBigIntToNumber(flow.packets_sent[1]);
            const rxDown = flow.tcp_retransmits[0] || 0;
            const rxUp = flow.tcp_retransmits[1] || 0;
            const isTcp = flow.ip_protocol === 6;
            const retransDownPct = isTcp && pktDown > 0 ? (rxDown / pktDown) : 0;
            const retransUpPct = isTcp && pktUp > 0 ? (rxUp / pktUp) : 0;

            let avgRate = down + up;
            if (this.movingAverages.has(flowKey)) {
                const arr = this.movingAverages.get(flowKey);
                avgRate = arr.reduce((a,b)=>a+b,0)/arr.length;
            }

            rows.push({
                sort: {
                    protocol: this.protoName(flow.ip_protocol),
                    rate: avgRate,
                    bytes: safeBigIntToNumber(flow.bytes_sent[0]) + safeBigIntToNumber(flow.bytes_sent[1]),
                    packets: pktDown + pktUp,
                    retransmits: retransDownPct + retransUpPct,
                    rtt: (flow.rtt_ms[0]||0) + (flow.rtt_ms[1]||0),
                    asn: flow.asn_name || "",
                    country: flow.asn_country || "",
                    ip: (flow.remote_ip_str || flow.remote_ip),
                },
                columns: [
                    this.protoName(flow.ip_protocol),
                    formatThroughput(down, 0),
                    formatThroughput(up, 0),
                    scaleNumber(safeBigIntToNumber(flow.bytes_sent[0])),
                    scaleNumber(safeBigIntToNumber(flow.bytes_sent[1])),
                    scaleNumber(pktDown),
                    scaleNumber(pktUp),
                    formatRetransmit(retransDownPct * 100, 1),
                    formatRetransmit(retransUpPct * 100, 1),
                    `${(flow.rtt_ms[0]||0).toFixed(0)}ms`,
                    `${(flow.rtt_ms[1]||0).toFixed(0)}ms`,
                    flow.asn_name || "",
                    flow.asn_country || "",
                    (flow.remote_ip_str || flow.remote_ip),
                ],
                opacity: 1.0 - Math.min(1, lastSeen / thirty_seconds_in_nanos),
                visible,
            });
        });

        rows.sort((a,b)=>{
            let aVal = a.sort[this.trafficSortColumn];
            let bVal = b.sort[this.trafficSortColumn];
            if (typeof aVal === 'string' && typeof bVal === 'string') { aVal=aVal.toLowerCase(); bVal=bVal.toLowerCase(); }
            return this.trafficSortDirection==='asc' ? (aVal<bVal?-1:(aVal>bVal?1:0)) : (aVal>bVal?-1:(aVal<bVal?1:0));
        });

        rows.forEach((r)=>{
            if (!r.visible) return;
            const tr = document.createElement('tr');
            tr.classList.add('small');
            tr.style.opacity = r.opacity;
            r.columns.forEach((col, idx) => {
                if (idx===1 || idx===2 || idx===7 || idx===8) tr.appendChild(simpleRowHtml(col));
                else tr.appendChild(simpleRow(col));
            });
            tbody.appendChild(tr);
        });

        table.appendChild(tbody);
        clearDiv(target);
        target.appendChild(table);
    }

    evictStaleFlowState(seenKeys) {
        const retentionTicks = 12;
        this.prevFlowBytes.forEach((prev, key) => {
            if (seenKeys.has(key)) {
                return;
            }
            const lastTick = Array.isArray(prev) ? prev[2] : 0;
            if ((this.tickCount - lastTick) > retentionTicks) {
                this.prevFlowBytes.delete(key);
            }
        });
        this.movingAverages.forEach((_avg, key) => {
            if (seenKeys.has(key)) {
                return;
            }
            const prev = this.prevFlowBytes.get(key);
            if (!prev || (this.tickCount - prev[2]) > retentionTicks) {
                this.movingAverages.delete(key);
            }
        });
    }
}

// Sankey graph for active flows (adapted from LibreQoS)
class FlowsSankey extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = {
            title: { text: 'Flows Sankey' },
            series: [
                {
                    type: 'sankey',
                    data: [],
                    links: [],
                    emphasis: { focus: 'adjacency' },
                    lineStyle: { color: 'gradient', curveness: 0.5 },
                    orient: 'horizontal',
                    layoutIterations: 0,
                }
            ]
        };
        this.option && this.chart.setOption(this.option);
        this.chart.hideLoading();
    }

    update(flows, ipToName) {
        // Accumulators
        let localDevices = {};
        let asns = {};
        let protocols = {};
        let remoteDevices = {};

        const ten_seconds_in_nanos = 10_000_000_000;
        let flowCount = 0;

        // Top 20 by instantaneous rate
        let sortedTop = flows
            .slice()
            .sort((a,b)=>{
                const ra = (a.rate_bps?.[0]||0) + (a.rate_bps?.[1]||0);
                const rb = (b.rate_bps?.[0]||0) + (b.rate_bps?.[1]||0);
                return rb - ra;
            })
            .slice(0, 20);

        const protoName = (id) => ({1:'ICMP',6:'TCP',17:'UDP'})[id] || String(id);

        sortedTop.forEach((flow) => {
            const lastNs = safeBigIntToNumber(flow.last_seen_nanos);
            if (lastNs > ten_seconds_in_nanos) return;
            flowCount++;
            let localIpStr = flow.local_ip_str || flow.local_ip;
            let localKey = ipToName?.get(String(localIpStr)) || String(localIpStr);
            let proto = protoName(flow.ip_protocol);
            let asn = 'ASN: ' + (flow.asn_name || flow.asn_country || 'Unknown');
            let remote = String(flow.remote_ip_str || flow.remote_ip);

            if (localDevices[localKey] === undefined) localDevices[localKey] = {};
            if (asns[asn] === undefined) asns[asn] = {};
            if (protocols[proto] === undefined) protocols[proto] = {};
            if (remoteDevices[remote] === undefined) remoteDevices[remote] = 0;

            const currentRate = (flow.rate_bps?.[0]||0) + (flow.rate_bps?.[1]||0);
            localDevices[localKey][asn] = (localDevices[localKey][asn] || 0) + currentRate;
            asns[asn][proto] = (asns[asn][proto] || 0) + currentRate;
            protocols[proto][remote] = (protocols[proto][remote] || 0) + currentRate;
        });

        // Build nodes and links
        let data = [];
        let links = [];

        for (let local in localDevices) {
            data.push({ name: local, label: applyRedactFont({ color: 'magenta' }) });
            for (let asn in localDevices[local]) {
                links.push({ source: local, target: asn, value: localDevices[local][asn] });
            }
        }
        for (let asn in asns) {
            data.push({ name: asn, label: { color: 'red' } });
            for (let proto in asns[asn]) {
                links.push({ source: asn, target: proto, value: asns[asn][proto] });
            }
        }
        for (let proto in protocols) {
            data.push({ name: proto, label: { color: 'green' } });
            for (let remote in protocols[proto]) {
                links.push({ source: proto, target: remote, value: protocols[proto][remote] });
            }
        }
        for (let remote in remoteDevices) {
            data.push({ name: remote, label: { color: 'orange' } });
        }

        this.option.series[0].data = data;
        this.option.series[0].links = links;
        this.chart.hideLoading();
        this.chart.setOption(this.option);
        return flowCount;
    }
}

class ThroughputGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.ringbuffer = new RingBuffer(60);

        const ringbuffer = this.ringbuffer;
        const formatTimeRef = formatTime;
        const scaleNumberRef = scaleNumber;
        this.option = this.option = {
            title: {
                text: this.title,
            },
            grid: { left: '20%' },
            legend: {
                orient: "horizontal",
                right: 10,
                top: "bottom",
                selectMode: false,
                data: [
                    {
                        name: "Download",
                        icon: 'circle',
                        itemStyle: {
                            color: window.graphPalette[0]
                        }
                    }, {
                        name: "Upload",
                        icon: 'circle',
                        itemStyle: {
                            color: window.graphPalette[1]
                        }
                    }
                ],
                textStyle: {
                    color: '#aaa'
                },
            },
            xAxis: {
                type: 'category',
                data: [] // will be set in update()
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (val) => {
                        return scaleNumber(Math.abs(val), 2);
                    },
                }
            },
            series: [
                {
                    name: 'Download',
                    data: [],
                    type: 'line',
                    lineStyle: {
                        color: window.graphPalette[0]
                    },
                    symbol: 'none',
                },
                {
                    name: 'Upload',
                    data: [],
                    type: 'line',
                    lineStyle: {
                        color: window.graphPalette[1]
                    },
                    symbol: 'none',
                },
            ],
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    label: {
                        backgroundColor: '#6a7985'
                    }
                },
                formatter: function(params) {
                    debugLog(params);
                    if (!params || params.length === 0) return '';
                    const idx = params[0].dataIndex;
                    const ts = ringbuffer.getTimestamp(idx);
                    let s = `<div><b>Time:</b> ${formatTimeRef(ts)}</div>`;
                    for (const p of params) {
                        s += `<div><span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:${p.color};"></span>${p.seriesName}: <b>${scaleNumber(Math.abs(p.value), 2)}</b></div>`;
                    }
                    return s;
                }
            },
        };
        
        this.option && this.chart.setOption(this.option);
    }

    update(download, upload) {
        this.ringbuffer.push(download, upload, Date.now());
        this.chart.hideLoading();
        let data = this.ringbuffer.series();
        let timestamps = this.ringbuffer.getTimestamps();
        let xLabels = timestamps.map(ts => formatTime(ts));

        this.option.series[0].data = data[0];
        this.option.series[1].data = data[1];
        this.option.xAxis.data = xLabels;

        this.chart.setOption(this.option);
    }
}

class RingBuffer {
    constructor(size) {
        this.size = size;
        let data = [];
        for (let i=0; i<size; i++) {
            data.push([0, 0, 0]);
        }
        this.head = 0;
        this.data = data;
    }

    push(download, upload, timestamp) {
        this.data[this.head][0] = download;
        this.data[this.head][1] = 0.0 - upload;
        this.data[this.head][2] = timestamp || Date.now();
        this.head += 1;
        this.head %= this.size;
    }

    series() {
        let result = [
            [], []
        ];
        for (let i=this.head; i<this.size; i++) {
            for (let j=0; j<2; j++) {
                result[j].push(this.data[i][j]);
            }
        }
        for (let i=0; i<this.head; i++) {
            for (let j=0; j<2; j++) {
                result[j].push(this.data[i][j]);
            }
        }
        return result;
    }

    getTimestamps() {
        let result = [];
        for (let i=this.head; i<this.size; i++) {
            result.push(this.data[i][2]);
        }
        for (let i=0; i<this.head; i++) {
            result.push(this.data[i][2]);
        }
        return result;
    }

    getTimestamp(idx) {
        // idx is relative to the current ringbuffer order
        // reconstruct the logical index
        let logicalIdx = (this.head + idx) % this.size;
        return this.data[logicalIdx][2];
    }
}

// Helper to format time as HH:MM:SS
function formatTime(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString();
}

const RING_SIZE = 60;

export class CircuitRetransmitGraph extends DashboardGraph {
    constructor(id, title) {
        super(id);
        this.title = title;
        this.ringbuffer = new RingBuffer(RING_SIZE);

        let xaxis = [];
        for (let i=0; i<RING_SIZE; i++) {
            xaxis.push(i);
        }

        this.option = {
            title: {
                text: this.title,
            },
            grid: { left: '20%' },
            legend: {
                orient: "horizontal",
                right: 10,
                top: "bottom",
                selectMode: false,
                data: [
                    {
                        name: "Download",
                        icon: 'circle',
                        itemStyle: {
                            color: window.graphPalette[0]
                        }
                    }, {
                        name: "Upload",
                        icon: 'circle',
                        itemStyle: {
                            color: window.graphPalette[1]
                        }
                    }
                ],
                textStyle: {
                    color: '#aaa'
                },
            },
            xAxis: {
                type: 'category',
                data: xaxis,
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (val) => {
                        return scaleNumber(Math.abs(val), 1) + "%";
                    },
                }
            },
            series: [
                {
                    name: 'Download',
                    data: [],
                    type: 'line',
                    lineStyle: {
                        color: window.graphPalette[0]
                    },
                    symbol: 'none',
                },
                {
                    name: 'Upload',
                    data: [],
                    type: 'line',
                    lineStyle: {
                        color: window.graphPalette[1]
                    },
                    symbol: 'none',
                },
            ],
            axisPointer: {
                type: 'cross'
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    label: {
                        backgroundColor: '#6a7985'
                    }
                },
                formatter: (params) => {
                    if (!params || params.length === 0) return '';
                    const idx = params[0].dataIndex;
                    const ts = this.ringbuffer.getTimestamp(idx);
                    let s = `<div><b>Time:</b> ${formatTime(ts)}</div>`;
                    for (const p of params) {
                        s += `<div><span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:${p.color};"></span>${p.seriesName}: <b>${Math.abs(p.value).toFixed(2)}%</b></div>`;
                    }
                    return s;
                }
            },
        }
        this.option && this.chart.setOption(this.option);
    }

    onThemeChange() {
        super.onThemeChange();
        this.option.legend.data[0].itemStyle.color = window.graphPalette[0];
        this.option.legend.data[1].itemStyle.color = window.graphPalette[1];
        this.option.series[0].lineStyle.color = window.graphPalette[0];
        this.option.series[1].lineStyle.color = window.graphPalette[1];
        this.chart.setOption(this.option);
    }

    update(download, upload) {
        this.chart.hideLoading();
        this.ringbuffer.push(download, upload);

        let data = this.ringbuffer.series();
        this.option.series[0].data = data[0];
        this.option.series[1].data = data[1];

        this.chart.setOption(this.option);
    }
}

class DevicePingHistogram extends DashboardGraph {
    constructor(id) {
        super(id);
        let d = [];
        let axis = [];
        for (let i=0; i<20; i++) {
            d.push({
                value: 0,
                itemStyle: {color: lerpGreenToRedViaOrange(20-i, 20)},
            });
            axis.push((i*10).toString());
        }
        this.option = {
            title: {
                text: "Ping Time Histogram",
            },
            xAxis: {
                type: 'category',
                data: axis,
                name: "Ping Time (ms)"
            },
            yAxis: {
                type: 'value',
                name: "Samples"
            },
            series: {
                data: d,
                type: 'bar',
            }
        };
        this.option && this.chart.setOption(this.option);
        this.chart.hideLoading();
    }

    update(ping_time_nanos) {
        this.chart.hideLoading();
        let ping_time_ms = ping_time_nanos / 1000000;
        let bucket = Math.floor(ping_time_ms / 10);
        bucket = Math.min(bucket, 19);
        this.option.series.data[bucket].value += 1;
        this.chart.setOption(this.option);
    }

    updateMs(ping_time_ms) {
        this.chart.hideLoading();
        let bucket = Math.floor(ping_time_ms / 10);
        bucket = Math.min(bucket, 19);
        this.option.series.data[bucket].value += 1;
        this.chart.setOption(this.option);
    }
}
